import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {FixedStrip} from '@/components/fixed-strip'
import {Tag} from '@/components/ui/tag'
import {MicroLabel} from '@/components/period-selector'
import {CheckGlyph} from '@/components/ui/icons'
import {type SgsstAssessment} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {BAND_TONE} from '@/lib/bands'
import {formatRatePct} from '@/lib/risk'
import {cn} from '@/lib/utils'

// Sin los actos de cierre: son el primario de la tab «Cierre» (R7). `reservaEspacio={false}`: la tira
// solo aparece cuando la tarjeta de puntaje ya se fue por arriba.
export function StickyProgressStrip({
  stuck,
  assessment,
  fase2,
  planHref,
}: {
  stuck: boolean
  assessment: SgsstAssessment
  fase2: boolean
  planHref: string
}) {
  const {t} = useTranslation()
  const score = assessment.score
  return (
    <FixedStrip
      inerte={!stuck}
      reservaEspacio={false}
      testId="sgsst-sticky-progress"
      className={cn(
        'z-40 pb-3',
        'motion-safe:transition-[opacity,transform] motion-safe:duration-200 motion-safe:ease-out',
        stuck ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-2 opacity-0'
      )}>
      <div className="mx-auto max-w-5xl">
        <div className="surface flex items-center justify-between gap-4 px-4 py-2.5 shadow-[var(--shadow-pop)]">
          <div className="flex min-w-0 items-center gap-4">
            {fase2 ? (
              <>
                <div>
                  <MicroLabel>{t('sgsst.dual.frozenLabel')}</MicroLabel>
                  <span className="mt-0.5 flex items-center gap-1 font-mono text-sm font-medium text-foreground">
                    <CheckGlyph />
                    {formatRatePct(assessment.snapshot_pct ?? 0)}
                  </span>
                </div>
                <div className="border-l border-border pl-4">
                  <MicroLabel>{t('sgsst.dual.liveLabel')}</MicroLabel>
                  <span className="mt-0.5 block font-mono text-sm font-medium text-primary">
                    {formatRatePct(score.total_pct)}
                  </span>
                </div>
              </>
            ) : (
              <div>
                <MicroLabel>{t('sgsst.dual.inProgressLabel')}</MicroLabel>
                <div className="mt-0.5 flex items-center gap-2">
                  <span className="font-mono text-sm font-medium text-primary">{formatRatePct(score.total_pct)}</span>
                  <Tag tone={BAND_TONE[score.band]}>{t(`sgsst.bands.${score.band}.label`)}</Tag>
                </div>
              </div>
            )}
          </div>
          {fase2 && (
            <div className="flex shrink-0 items-center gap-3">
              <Link
                to={planHref}
                data-testid="plan-link-sticky"
                className="hidden text-sm font-medium text-primary hover:underline sm:inline">
                {t('sgsst.planLink')}
              </Link>
            </div>
          )}
        </div>
      </div>
    </FixedStrip>
  )
}
